import type { ReactNode } from 'react';

export type PillTone =
  | 'warning'
  | 'info'
  | 'secondary'
  | 'success'
  | 'successSolid'
  | 'danger'
  | 'accent'
  | 'neutral'
  | 'primary';

const toneClasses: Record<PillTone, string> = {
  warning: 'bg-warning-500/15 text-warning-700',
  info: 'bg-info-500/15 text-info-700',
  secondary: 'bg-secondary-500/15 text-secondary-700',
  success: 'bg-success-500/15 text-success-700',
  successSolid: 'bg-success-500 text-white',
  danger: 'bg-danger-500/15 text-danger-700',
  accent: 'bg-accent-500/15 text-accent-700',
  neutral: 'bg-background-200 text-foreground-500',
  primary: 'bg-primary-100 text-primary-700',
};

interface PillProps {
  tone?: PillTone;
  icon?: string;
  className?: string;
  children: ReactNode;
}

export function Pill({ tone = 'neutral', icon, className = '', children }: PillProps) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold',
        toneClasses[tone],
        className,
      ].join(' ')}
    >
      {icon && <i className={`${icon} text-[12px] leading-none`} aria-hidden="true"></i>}
      {children}
    </span>
  );
}

export type OrderStatus =
  | 'pending'
  | 'accepted'
  | 'processing'
  | 'ready'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | 'needs_driver';

export const orderStatusConfig: Record<OrderStatus, { label: string; tone: PillTone }> = {
  pending: { label: 'بانتظار القبول', tone: 'warning' },
  accepted: { label: 'تم القبول', tone: 'info' },
  processing: { label: 'قيد التنفيذ', tone: 'secondary' },
  ready: { label: 'جاهز للتسليم', tone: 'success' },
  completed: { label: 'مكتمل', tone: 'successSolid' },
  cancelled: { label: 'ملغي', tone: 'danger' },
  rejected: { label: 'مرفوض', tone: 'danger' },
  needs_driver: { label: 'يحتاج مندوب', tone: 'accent' },
};

export function StatusBadge({ status }: { status: OrderStatus }) {
  const config = orderStatusConfig[status];
  return <Pill tone={config.tone}>{config.label}</Pill>;
}

export type Priority = 'normal' | 'urgent';

export function PriorityBadge({ priority }: { priority: Priority }) {
  if (priority === 'urgent') {
    return (
      <Pill tone="accent" icon="ri-flashlight-fill" className="bg-accent-500 text-foreground-950">
        مستعجل
      </Pill>
    );
  }
  return <Pill tone="secondary">عادي</Pill>;
}

export type OrderSource = 'direct' | 'package';

export function SourceBadge({ source }: { source: OrderSource }) {
  if (source === 'package') {
    return <Pill className="bg-secondary-200 text-primary-700">باقة</Pill>;
  }
  return <Pill tone="neutral">مباشر</Pill>;
}